/**
 * /tracking/deferred-deep-link — operator page for feature #177.
 *
 * Declares a TanStack Router route and renders `DeferredDeepLinkPage`. The
 * component is imported by the root-owned `router.tsx`; agents never touch
 * `router.tsx` themselves — the root wire-up cycle picks this route up after
 * the prod merge.
 *
 * `?fingerprint=` and `?campaignId=` deep-link the page pre-filtered so
 * operators can jump here from a click detail view or a campaign summary.
 */

import { Link, createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { DeferredDeepLinkPage } from "@/features/deferred-deep-link/DeferredDeepLinkPage";

interface TrackingDeferredDeepLinkSearch {
	fingerprint?: string;
	campaignId?: string;
}

export const trackingDeferredDeepLinkRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/deferred-deep-link",
	validateSearch: (
		raw: Record<string, unknown>,
	): TrackingDeferredDeepLinkSearch => {
		const out: TrackingDeferredDeepLinkSearch = {};
		if (typeof raw.fingerprint === "string" && raw.fingerprint.trim().length > 0) {
			out.fingerprint = raw.fingerprint.trim();
		}
		if (typeof raw.campaignId === "string" && raw.campaignId.trim().length > 0) {
			out.campaignId = raw.campaignId.trim();
		}
		return out;
	},
	component: TrackingDeferredDeepLinkRoute,
});

export default trackingDeferredDeepLinkRoute;

export function TrackingDeferredDeepLinkRoute() {
	const { fingerprint, campaignId } =
		trackingDeferredDeepLinkRoute.useSearch();

	return (
		<div className="flex flex-col">
			<nav
				aria-label="Breadcrumb"
				className="mx-auto w-full max-w-5xl px-6 pt-10 text-sm text-zinc-500 sm:pt-16"
			>
				<ol className="flex flex-wrap items-center gap-1">
					<li>
						<Link
							to="/"
							className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
						>
							Dashboard
						</Link>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li>
						<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
					</li>
					<li aria-hidden="true" className="text-zinc-300">
						/
					</li>
					<li className="text-zinc-900">Deferred deep links</li>
				</ol>
			</nav>

			<DeferredDeepLinkPage
				initialFingerprint={fingerprint}
				initialCampaignId={campaignId}
			/>
		</div>
	);
}

// Named export for the root wire-up cycle.
export { TrackingDeferredDeepLinkRoute as TrackingDeferredDeepLinkPage };
